/**
 * TeacherSidebar (v6.32.102) — Left-rail navigation for the Teacher
 * Dashboard.
 *
 * Replaces the v6.32.101 horizontal 21-tab `<TabsList>` strip with a
 * task-based grouped sidebar (same pattern as v6.32.99
 * SuperAdminSidebar). 21 tabs flex-wrapped over 3+ rows is past
 * Miller's 7±2; grouping into 5 task-named sections collapses initial
 * cognitive load to ~5 choices and keeps related concepts (Daily Work,
 * Insights, Resources) together.
 *
 *   - 5 task-named groups (Daily Work, Class Setup, Insights,
 *     Resources, Operations).
 *   - Sticky search bar at top — recognition over recall.
 *   - Default state: only the group containing the currently active
 *     tab is expanded.
 *   - Mobile (<1024px): collapses to a hamburger drawer.
 *   - Every existing data-testid is preserved verbatim so the test
 *     suite keeps passing.
 *
 * The Radix `<Tabs>` / `<TabsContent>` infrastructure in
 * TeacherDashboard.js is unchanged — only the visual rendering of the
 * tab list itself is replaced.
 */
import React, { useState, useMemo, useEffect } from 'react';
import {
  BarChart3,
  Briefcase,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Clock,
  Database,
  FolderOpen,
  GitCompare,
  GraduationCap,
  Home,
  Layers,
  LayoutDashboard,
  Menu,
  MessageSquareHeart,
  PenTool,
  PlusCircle,
  Search,
  Shield,
  TrendingUp,
  Users2,
  Wrench,
  X,
} from 'lucide-react';

/**
 * The canonical 5-group taxonomy. Adding a new tab? Place it in the
 * group that matches the TEACHER'S TASK (what they're trying to do).
 *
 * Note: `demo-data` is super-admin / district-admin / school-admin /
 * teacher gated upstream; it lives under Operations.
 */
export const TEACHER_TAB_GROUPS = [
  {
    id: 'daily',
    label: 'Daily Work',
    icon: LayoutDashboard,
    tabs: [
      { value: 'overview', label: 'Overview', icon: Home, testId: 'tch-overview-tab' },
      { value: 'assignments', label: 'Assignments', icon: ClipboardCheck, testId: 'assignments-tab' },
      { value: 'grading', label: 'Grading', icon: PenTool, testId: 'pending-grading-tab' },
      { value: 'results', label: 'Recent Results', icon: TrendingUp, testId: 'tch-results-tab' },
    ],
  },
  {
    id: 'classes',
    label: 'Class Setup',
    icon: FolderOpen,
    tabs: [
      { value: 'classes', label: 'Classes', icon: FolderOpen, testId: 'class-management-tab' },
      { value: 'class-groups', label: 'Class Groups', icon: Users2, testId: 'class-groups-tab' },
      { value: 'custom-questions', label: 'My Questions', icon: PlusCircle, testId: 'custom-questions-tab' },
      { value: 'windows', label: 'Assessment Windows', icon: Clock, testId: 'assessment-windows-tab' },
    ],
  },
  {
    id: 'insights',
    label: 'Insights & Analytics',
    icon: BarChart3,
    tabs: [
      { value: 'analytics', label: 'Analytics', icon: BarChart3, testId: 'analytics-tab' },
      { value: 'growth', label: 'Growth', icon: TrendingUp, testId: 'growth-tab' },
      { value: 'cohort', label: 'Cohort', icon: Layers, testId: 'cohort-tab' },
      { value: 'projections', label: 'Projections', icon: TrendingUp, testId: 'projections-tab' },
      { value: 'wida-standards', label: 'ELD Standards', icon: GraduationCap, testId: 'wida-standards-tab' },
    ],
  },
  {
    id: 'resources',
    label: 'Teaching Resources',
    icon: Briefcase,
    tabs: [
      { value: 'resources', label: 'Teacher Resources', icon: Briefcase, testId: 'teacher-resources-tab' },
      { value: 'lesson-plans', label: 'Lesson Plans', icon: ClipboardCheck, testId: 'lesson-plans-tab' },
      { value: 'quick-checks', label: 'Quick Checks', icon: ClipboardCheck, testId: 'quick-checks-tab' },
      { value: 'toolkit', label: 'Toolkit', icon: Briefcase, testId: 'toolkit-tab' },
      { value: 'crosswalk', label: 'Program Crosswalk', icon: GitCompare, testId: 'crosswalk-tab' },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    icon: Wrench,
    tabs: [
      { value: 'integrity', label: 'Exam Integrity', icon: Shield, testId: 'exam-integrity-tab' },
      { value: 'feedback', label: 'Beta Feedback', icon: MessageSquareHeart, testId: 'feedback-tab' },
      { value: 'demo-data', label: 'Demo Data', icon: Database, testId: 'demo-data-tab' },
    ],
  },
];

const findGroupForTab = (tabValue) => {
  for (const group of TEACHER_TAB_GROUPS) {
    if (group.tabs.some((t) => t.value === tabValue)) return group.id;
  }
  return TEACHER_TAB_GROUPS[0].id;
};

const TeacherSidebar = ({ activeTab, onTabChange, badges = {} }) => {
  const [search, setSearch] = useState('');
  const [openGroups, setOpenGroups] = useState(() => ({
    [findGroupForTab(activeTab)]: true,
  }));
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const groupId = findGroupForTab(activeTab);
    setOpenGroups((prev) => (prev[groupId] ? prev : { ...prev, [groupId]: true }));
  }, [activeTab]);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return TEACHER_TAB_GROUPS;
    return TEACHER_TAB_GROUPS
      .map((group) => {
        const matchedTabs = group.tabs.filter(
          (t) =>
            t.label.toLowerCase().includes(q) ||
            t.value.toLowerCase().includes(q) ||
            group.label.toLowerCase().includes(q),
        );
        return { ...group, tabs: matchedTabs };
      })
      .filter((group) => group.tabs.length > 0);
  }, [search]);

  const effectiveOpenGroups = useMemo(() => {
    if (!search.trim()) return openGroups;
    const all = {};
    filteredGroups.forEach((g) => { all[g.id] = true; });
    return all;
  }, [search, openGroups, filteredGroups]);

  const handleTabClick = (tabValue) => {
    onTabChange(tabValue);
    setMobileOpen(false);
  };

  const toggleGroup = (groupId) => {
    setOpenGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const sidebarContent = (
    <>
      <div className="px-4 pt-4 pb-3 border-b border-gray-200">
        <div className="relative" data-testid="tch-sidebar-search-wrap">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tabs…"
            aria-label="Search Teacher Dashboard tabs"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#002FA7] focus:outline-none transition-colors"
            data-testid="tch-sidebar-search"
          />
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-2" aria-label="Teacher Dashboard navigation">
        {filteredGroups.length === 0 && (
          <div className="px-4 py-6 text-sm text-gray-500 text-center" data-testid="tch-sidebar-no-results">
            No tabs match "<span className="font-mono">{search}</span>"
          </div>
        )}
        {filteredGroups.map((group) => {
          const GroupIcon = group.icon;
          const isOpen = !!effectiveOpenGroups[group.id];
          return (
            <div key={group.id} className="mb-1">
              <button
                type="button"
                onClick={() => toggleGroup(group.id)}
                className="w-full flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-wider text-gray-500 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                aria-expanded={isOpen}
                data-testid={`tch-sidebar-group-${group.id}`}
              >
                <GroupIcon size={14} />
                <span className="flex-1 text-left">{group.label}</span>
                {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
              {isOpen && (
                <ul className="mt-0.5 mb-2">
                  {group.tabs.map((tab) => {
                    const TabIcon = tab.icon;
                    const isActive = activeTab === tab.value;
                    const badge = badges[tab.value];
                    return (
                      <li key={tab.value}>
                        <button
                          type="button"
                          onClick={() => handleTabClick(tab.value)}
                          className={`w-full flex items-center gap-2 pl-10 pr-4 py-2 text-sm transition-colors border-l-2 ${
                            isActive
                              ? 'bg-blue-50 text-[#002FA7] font-semibold border-[#002FA7]'
                              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 border-transparent'
                          }`}
                          aria-current={isActive ? 'page' : undefined}
                          data-testid={tab.testId}
                        >
                          <TabIcon size={14} className="flex-shrink-0" />
                          <span className="text-left flex-1">{tab.label}</span>
                          {badge && (
                            <span className="ml-1 px-2 py-0.5 text-[10px] bg-orange-500 text-white rounded-full font-bold animate-pulse">
                              {badge}
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </nav>
    </>
  );

  return (
    <>
      {/* v6.34.14 — Top-left hamburger (primary mobile trigger, visible <768px) */}
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        className="md:hidden fixed top-3 left-3 z-30 flex items-center justify-center w-11 h-11 bg-[#002FA7] text-white shadow-lg rounded-lg"
        aria-label="Open Teacher Dashboard navigation"
        data-testid="tch-sidebar-mobile-open-top"
      >
        <Menu size={22} />
      </button>

      {/* Mobile hamburger toggle (secondary — bottom-right floating pill, visible <768px) */}
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        className="md:hidden fixed bottom-6 right-6 z-30 flex items-center gap-2 px-4 py-3 bg-[#002FA7] text-white shadow-lg rounded-full"
        aria-label="Open Teacher Dashboard navigation"
        data-testid="tch-sidebar-mobile-open"
      >
        <Menu size={18} />
        <span className="text-sm font-semibold">Menu</span>
      </button>

      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={`md:hidden fixed top-0 left-0 bottom-0 w-72 bg-white z-50 shadow-xl transition-transform duration-200 flex flex-col ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="tch-sidebar-mobile"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <span className="text-sm font-bold uppercase tracking-wide text-[#002FA7]">Teacher</span>
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="p-1 hover:bg-gray-100 rounded"
            aria-label="Close navigation"
            data-testid="tch-sidebar-mobile-close"
          >
            <X size={18} />
          </button>
        </div>
        {sidebarContent}
      </aside>

      <aside
        className="hidden md:flex flex-col w-60 flex-shrink-0 bg-white border-r border-gray-200 sticky top-0 self-start"
        style={{ height: 'calc(100vh - 4rem)' }}
        data-testid="tch-sidebar-desktop"
      >
        {sidebarContent}
      </aside>
    </>
  );
};

export default TeacherSidebar;
